import React, { useEffect } from 'react';

const ObjectStorage = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Object Storage & S3 Model - Scalable Storage Architecture 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master object storage systems like AWS S3. Learn architecture, APIs, consistency models, and implementation patterns for scalable storage.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master object storage systems like AWS S3. Learn architecture, APIs, consistency models, and implementation patterns for scalable storage.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Object Storage and the S3 Model</h1>
        <p style={styles.subtitle}>
          Build scalable, durable storage systems with object storage architecture patterns
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Object Storage</h2>
          <p style={styles.paragraph}>
            Object storage is a data storage architecture that manages data as objects, rather than files 
            in a hierarchy or blocks in sectors. Each object contains the data itself, metadata, and a 
            unique identifier. This approach provides massive scalability, durability, and accessibility 
            that traditional file systems cannot match.
          </p>
          <p style={styles.paragraph}>
            Amazon S3 (Simple Storage Service) popularized the object storage model and became the de facto 
            standard for cloud storage APIs. Understanding the S3 model is crucial for designing modern 
            distributed applications that need to store and retrieve large amounts of unstructured data.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Object Storage Fundamentals</h2>
          
          <h3 style={styles.heading}>Core Concepts</h3>
          <p style={styles.paragraph}>
            Object storage systems organize data around several key concepts that differ fundamentally 
            from traditional file systems.
          </p>
          <pre style={styles.codeBlock}>
{`Object Storage Core Concepts:

1. Objects:
   - Basic unit of storage
   - Contains data + metadata + unique key
   - Immutable (typically replaced, not modified)
   - Size: 0 bytes to 5TB (S3 limit)

2. Buckets/Containers:
   - Top-level containers for objects
   - Globally unique names
   - Access control boundary
   - Configuration unit (versioning, lifecycle, etc.)

3. Keys:
   - Unique identifier within a bucket
   - Often resembles file path: "folder/subfolder/file.txt"
   - No actual folder hierarchy (flat namespace)
   - Case-sensitive strings

4. Metadata:
   - System metadata: size, last-modified, etag, etc.
   - User metadata: custom key-value pairs
   - HTTP headers for web access
   - Searchable in some systems

5. Regions:
   - Geographic locations for data storage
   - Affects latency, compliance, costs
   - Cross-region replication available

Object Structure Example:
{
  "bucket": "my-app-data",
  "key": "users/123/profile.jpg",
  "data": "<binary image data>",
  "metadata": {
    "Content-Type": "image/jpeg",
    "Content-Length": 1024567,
    "Last-Modified": "2024-01-15T10:30:00Z",
    "ETag": "d41d8cd98f00b204e9800998ecf8427e",
    "x-amz-meta-user-id": "123",
    "x-amz-meta-upload-source": "mobile-app"
  },
  "acl": "private"
}

Benefits of Object Storage:
✅ Massive scalability (exabytes)
✅ High durability (99.999999999% - "11 9's")
✅ Global accessibility via HTTP/REST
✅ Rich metadata support
✅ Built-in redundancy
✅ Cost-effective for large datasets
✅ No file system limits

Limitations:
❌ Not suitable for frequent updates
❌ Higher latency than block storage
❌ No partial object updates
❌ No POSIX file system semantics
❌ Eventual consistency in some operations`}
          </pre>

          <h3 style={styles.heading}>S3 API Model</h3>
          <p style={styles.paragraph}>
            The S3 API has become the standard interface for object storage, with most cloud providers 
            offering S3-compatible APIs. Understanding this API is essential for building portable applications.
          </p>
          <pre style={styles.codeBlock}>
{`S3 API Operations:

Bucket Operations:
- CREATE BUCKET: Create new storage container
- DELETE BUCKET: Remove empty bucket
- LIST BUCKETS: Get all buckets for account
- GET BUCKET LOCATION: Determine bucket region
- PUT BUCKET POLICY: Set access permissions
- GET BUCKET VERSIONING: Check version configuration

Object Operations:
- PUT OBJECT: Upload new object
- GET OBJECT: Download object data
- DELETE OBJECT: Remove object
- HEAD OBJECT: Get object metadata only
- COPY OBJECT: Copy between locations
- LIST OBJECTS: Get objects in bucket

Advanced Operations:
- MULTIPART UPLOAD: Upload large objects in parts
- PRESIGNED URLs: Temporary access without credentials
- OBJECT TAGGING: Add searchable tags
- OBJECT LIFECYCLE: Automated data management
- CROSS-REGION REPLICATION: Geographic redundancy

REST API Examples:

# Create bucket
PUT /my-bucket HTTP/1.1
Host: s3.amazonaws.com
Authorization: AWS4-HMAC-SHA256 ...

# Upload object
PUT /my-bucket/path/to/file.txt HTTP/1.1
Host: s3.amazonaws.com
Content-Type: text/plain
Content-Length: 1024
x-amz-meta-user-id: 123

<file content>

# Download object
GET /my-bucket/path/to/file.txt HTTP/1.1
Host: s3.amazonaws.com
Authorization: AWS4-HMAC-SHA256 ...

# List objects
GET /my-bucket?list-type=2&prefix=path/to/ HTTP/1.1
Host: s3.amazonaws.com

# Delete object
DELETE /my-bucket/path/to/file.txt HTTP/1.1
Host: s3.amazonaws.com
Authorization: AWS4-HMAC-SHA256 ...

Response Format:
<?xml version="1.0" encoding="UTF-8"?>
<ListBucketResult>
    <Name>my-bucket</Name>
    <Prefix>path/to/</Prefix>
    <Contents>
        <Key>path/to/file1.txt</Key>
        <LastModified>2024-01-15T10:30:00.000Z</LastModified>
        <ETag>"d41d8cd98f00b204e9800998ecf8427e"</ETag>
        <Size>1024</Size>
        <StorageClass>STANDARD</StorageClass>
    </Contents>
</ListBucketResult>`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Object Storage Architecture</h2>
          
          <h3 style={styles.heading}>Distributed Storage System</h3>
          <p style={styles.paragraph}>
            Object storage systems are built as distributed systems that can scale horizontally across 
            thousands of servers while maintaining high availability and durability.
          </p>
          <pre style={styles.codeBlock}>
{`Object Storage System Architecture:

┌─────────────────────────────────────────────────────────────┐
│                        Load Balancer                        │
└─────────────────────────┬───────────────────────────────────┘
                          │
┌─────────────────────────┴───────────────────────────────────┐
│                    API Gateway Layer                       │
│  ┌─────────────┐ ┌─────────────┐ ┌─────────────┐          │
│  │   Auth &    │ │   Request   │ │   Response  │          │
│  │    Authz    │ │  Validation │ │   Format    │          │
│  └─────────────┘ └─────────────┘ └─────────────┘          │
└─────────────────────────┬───────────────────────────────────┘
                          │
┌─────────────────────────┴───────────────────────────────────┐
│                   Metadata Layer                           │
│  ┌─────────────────────────────────────────────────────────┐│
│  │              Metadata Database                          ││
│  │   - Object locations and replicas                      ││
│  │   - Bucket configurations                               ││
│  │   - Access control lists                                ││
│  │   - Object metadata and tags                            ││
│  └─────────────────────────────────────────────────────────┘│
└─────────────────────────┬───────────────────────────────────┘
                          │
┌─────────────────────────┴───────────────────────────────────┐
│                    Storage Layer                           │
│                                                            │
│  ┌─────────────┐ ┌─────────────┐ ┌─────────────┐          │
│  │   Node 1    │ │   Node 2    │ │   Node N    │          │
│  │             │ │             │ │             │          │
│  │ ┌─────────┐ │ │ ┌─────────┐ │ │ ┌─────────┐ │          │
│  │ │ Replica │ │ │ │ Replica │ │ │ │ Replica │ │          │
│  │ │    A    │ │ │ │    B    │ │ │ │    C    │ │          │
│  │ └─────────┘ │ │ └─────────┘ │ │ └─────────┘ │          │
│  │ ┌─────────┐ │ │ ┌─────────┐ │ │ ┌─────────┐ │          │
│  │ │ Replica │ │ │ │ Replica │ │ │ │ Replica │ │          │
│  │ │    D    │ │ │ │    E    │ │ │ │    F    │ │          │
│  │ └─────────┘ │ │ └─────────┘ │ │ └─────────┘ │          │
│  └─────────────┘ └─────────────┘ └─────────────┘          │
└────────────────────────────────────────────────────────────┘

Key Components:

1. API Gateway:
   - HTTP/REST endpoint
   - Authentication & authorization
   - Request routing and load balancing
   - Response formatting (XML/JSON)
   - Rate limiting and throttling

2. Metadata Service:
   - Object location tracking
   - Bucket and object metadata
   - Access control policies
   - Versioning information
   - Distributed database (often NoSQL)

3. Storage Nodes:
   - Actual data storage
   - Local file systems or block storage
   - Replication and erasure coding
   - Health monitoring
   - Automatic failure recovery

4. Data Placement:
   - Consistent hashing for distribution
   - Replication factor (typically 3)
   - Rack/zone awareness
   - Automatic rebalancing

Consistency Model:
┌──────────────────────────────────────────────────────────────┐
│                    Consistency Levels                       │
├──────────────────────────────────────────────────────────────┤
│ Strong Consistency:                                          │
│ - New objects: Immediate read-after-write                    │
│ - Object metadata: Strongly consistent                       │
│                                                              │
│ Eventual Consistency:                                        │
│ - Object updates/deletes: May take time to propagate        │
│ - Cross-region replication: Asynchronous                    │
│ - Listing operations: May not immediately reflect changes   │
└──────────────────────────────────────────────────────────────┘`}
          </pre>

          <h3 style={styles.heading}>Durability and Replication</h3>
          <p style={styles.paragraph}>
            Object storage systems achieve extremely high durability through sophisticated replication 
            and erasure coding strategies that protect against multiple types of failures.
          </p>
          <pre style={styles.codeBlock}>
{`Durability Strategies:

1. Multi-Copy Replication:
   - Store 3+ copies of each object
   - Distribute across different nodes/racks/zones
   - Synchronous or asynchronous replication
   - Simple but storage-expensive

Example Replication:
Object "user/123/photo.jpg":
- Primary copy: Node A, Rack 1, Zone US-East-1a
- Replica 1:   Node B, Rack 2, Zone US-East-1b  
- Replica 2:   Node C, Rack 3, Zone US-East-1c

2. Erasure Coding:
   - Split object into N data chunks
   - Generate M parity chunks
   - Can recover from M failures
   - More storage efficient than replication

Example: Reed-Solomon (6+3):
Original Object (6MB) → Split into 6 chunks (1MB each)
Generate 3 parity chunks → Total 9 chunks
Can lose any 3 chunks and still recover

Storage Efficiency:
- Replication (3x): 300% storage overhead
- Erasure Coding (6+3): 150% storage overhead

3. Cross-Region Replication:
   - Replicate to different geographic regions
   - Protects against regional disasters
   - Reduces latency for global users
   - Compliance with data residency laws

Durability Calculation:
Annual Failure Rate (AFR) per drive: 0.4%
With 3-way replication across zones:
- Probability of single drive failure: 0.004
- Probability of 3 simultaneous failures: 0.004³ = 6.4 × 10⁻⁸
- Durability: 99.999999936% (10+ nines)

4. Consistency and Repair:
   - Background consistency checking
   - Automatic repair of corrupted/missing replicas
   - Checksums for data integrity verification
   - Proactive replacement of failing drives

Repair Process:
1. Detect missing/corrupted replica
2. Identify healthy replicas
3. Create new replica on different node
4. Verify integrity of new replica
5. Update metadata with new location

class ReplicationManager:
    def __init__(self, replication_factor=3):
        self.replication_factor = replication_factor
        self.nodes = self.get_available_nodes()
    
    def store_object(self, object_key, data):
        # Choose nodes for replicas
        target_nodes = self.select_nodes_for_replication(
            self.replication_factor
        )
        
        replicas = []
        for node in target_nodes:
            try:
                replica_id = node.store(object_key, data)
                replicas.append({
                    'node_id': node.id,
                    'replica_id': replica_id,
                    'checksum': self.calculate_checksum(data)
                })
            except Exception as e:
                # Handle storage failure
                self.handle_storage_failure(node, e)
        
        if len(replicas) < self.replication_factor:
            # Not enough replicas created
            self.create_additional_replicas(object_key, data, replicas)
        
        # Update metadata with replica locations
        self.metadata_service.store_object_metadata(
            object_key, replicas, len(data)
        )
    
    def select_nodes_for_replication(self, count):
        # Select nodes across different failure domains
        selected = []
        zones_used = set()
        
        for node in self.nodes:
            if len(selected) >= count:
                break
            
            # Prefer nodes in different zones
            if node.zone not in zones_used:
                selected.append(node)
                zones_used.add(node.zone)
        
        # Fill remaining slots if needed
        while len(selected) < count and len(selected) < len(self.nodes):
            for node in self.nodes:
                if node not in selected:
                    selected.append(node)
                    break
        
        return selected`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Patterns</h2>
          
          <h3 style={styles.heading}>Building an S3-Compatible Service</h3>
          <p style={styles.paragraph}>
            Creating an S3-compatible object storage service requires implementing the core API operations, 
            metadata management, and storage backend while maintaining compatibility with existing S3 clients.
          </p>
          <pre style={styles.codeBlock}>
{`S3-Compatible Service Implementation:

# Python Flask implementation
from flask import Flask, request, Response, jsonify
import hashlib
import time
import json
import os
from datetime import datetime
import xml.etree.ElementTree as ET

class S3CompatibleStorage:
    def __init__(self, storage_backend):
        self.app = Flask(__name__)
        self.storage = storage_backend
        self.metadata_db = MetadataDatabase()
        self.setup_routes()
    
    def setup_routes(self):
        # Bucket operations
        self.app.route('/', methods=['GET'])(self.list_buckets)
        self.app.route('/<bucket>', methods=['PUT'])(self.create_bucket)
        self.app.route('/<bucket>', methods=['DELETE'])(self.delete_bucket)
        self.app.route('/<bucket>', methods=['GET'])(self.list_objects)
        
        # Object operations
        self.app.route('/<bucket>/<path:key>', methods=['PUT'])(self.put_object)
        self.app.route('/<bucket>/<path:key>', methods=['GET'])(self.get_object)
        self.app.route('/<bucket>/<path:key>', methods=['DELETE'])(self.delete_object)
        self.app.route('/<bucket>/<path:key>', methods=['HEAD'])(self.head_object)
    
    def list_buckets(self):
        """List all buckets"""
        try:
            buckets = self.metadata_db.list_buckets()
            
            # Create XML response
            root = ET.Element('ListAllMyBucketsResult')
            owner = ET.SubElement(root, 'Owner')
            ET.SubElement(owner, 'ID').text = 'owner-id'
            ET.SubElement(owner, 'DisplayName').text = 'owner'
            
            buckets_elem = ET.SubElement(root, 'Buckets')
            for bucket in buckets:
                bucket_elem = ET.SubElement(buckets_elem, 'Bucket')
                ET.SubElement(bucket_elem, 'Name').text = bucket['name']
                ET.SubElement(bucket_elem, 'CreationDate').text = bucket['created']
            
            xml_response = ET.tostring(root, encoding='unicode')
            return Response(xml_response, mimetype='application/xml')
            
        except Exception as e:
            return self.error_response('InternalError', str(e), 500)
    
    def create_bucket(self, bucket):
        """Create a new bucket"""
        try:
            # Validate bucket name
            if not self.is_valid_bucket_name(bucket):
                return self.error_response(
                    'InvalidBucketName', 
                    f'Invalid bucket name: {bucket}', 
                    400
                )
            
            # Check if bucket already exists
            if self.metadata_db.bucket_exists(bucket):
                return self.error_response(
                    'BucketAlreadyExists',
                    f'Bucket {bucket} already exists',
                    409
                )
            
            # Create bucket
            bucket_info = {
                'name': bucket,
                'created': datetime.utcnow().isoformat() + 'Z',
                'region': request.headers.get('x-amz-bucket-region', 'us-east-1')
            }
            
            self.metadata_db.create_bucket(bucket_info)
            
            return Response('', status=200)
            
        except Exception as e:
            return self.error_response('InternalError', str(e), 500)
    
    def put_object(self, bucket, key):
        """Upload an object"""
        try:
            # Validate bucket exists
            if not self.metadata_db.bucket_exists(bucket):
                return self.error_response('NoSuchBucket', f'Bucket {bucket} does not exist', 404)
            
            # Get object data
            data = request.get_data()
            content_type = request.headers.get('Content-Type', 'binary/octet-stream')
            
            # Calculate ETag (MD5 hash)
            etag = hashlib.md5(data).hexdigest()
            
            # Extract metadata from headers
            metadata = {}
            for header, value in request.headers:
                if header.lower().startswith('x-amz-meta-'):
                    metadata[header[11:]] = value
            
            # Store object data
            storage_key = f"{bucket}/{key}"
            self.storage.put(storage_key, data)
            
            # Store object metadata
            object_info = {
                'bucket': bucket,
                'key': key,
                'size': len(data),
                'etag': etag,
                'content_type': content_type,
                'last_modified': datetime.utcnow().isoformat() + 'Z',
                'metadata': metadata,
                'storage_key': storage_key
            }
            
            self.metadata_db.put_object(object_info)
            
            # Return success response
            response = Response('', status=200)
            response.headers['ETag'] = f'"{etag}"'
            return response
            
        except Exception as e:
            return self.error_response('InternalError', str(e), 500)
    
    def get_object(self, bucket, key):
        """Download an object"""
        try:
            # Get object metadata
            obj_info = self.metadata_db.get_object(bucket, key)
            if not obj_info:
                return self.error_response('NoSuchKey', f'Key {key} does not exist', 404)
            
            # Get object data from storage
            data = self.storage.get(obj_info['storage_key'])
            if data is None:
                return self.error_response('NoSuchKey', f'Object data not found', 404)
            
            # Create response
            response = Response(data)
            response.headers['Content-Type'] = obj_info['content_type']
            response.headers['Content-Length'] = str(obj_info['size'])
            response.headers['ETag'] = f'"{obj_info["etag"]}"'
            response.headers['Last-Modified'] = obj_info['last_modified']
            
            # Add custom metadata headers
            for key, value in obj_info['metadata'].items():
                response.headers[f'x-amz-meta-{key}'] = value
            
            return response
            
        except Exception as e:
            return self.error_response('InternalError', str(e), 500)
    
    def list_objects(self, bucket):
        """List objects in bucket"""
        try:
            if not self.metadata_db.bucket_exists(bucket):
                return self.error_response('NoSuchBucket', f'Bucket {bucket} does not exist', 404)
            
            # Get query parameters
            prefix = request.args.get('prefix', '')
            max_keys = int(request.args.get('max-keys', 1000))
            marker = request.args.get('marker', '')
            
            # Get objects from metadata database
            objects = self.metadata_db.list_objects(bucket, prefix, max_keys, marker)
            
            # Create XML response
            root = ET.Element('ListBucketResult')
            ET.SubElement(root, 'Name').text = bucket
            ET.SubElement(root, 'Prefix').text = prefix
            ET.SubElement(root, 'MaxKeys').text = str(max_keys)
            ET.SubElement(root, 'IsTruncated').text = 'false'
            
            for obj in objects:
                contents = ET.SubElement(root, 'Contents')
                ET.SubElement(contents, 'Key').text = obj['key']
                ET.SubElement(contents, 'LastModified').text = obj['last_modified']
                ET.SubElement(contents, 'ETag').text = f'"{obj["etag"]}"'
                ET.SubElement(contents, 'Size').text = str(obj['size'])
                ET.SubElement(contents, 'StorageClass').text = 'STANDARD'
            
            xml_response = ET.tostring(root, encoding='unicode')
            return Response(xml_response, mimetype='application/xml')
            
        except Exception as e:
            return self.error_response('InternalError', str(e), 500)
    
    def delete_object(self, bucket, key):
        """Delete an object"""
        try:
            # Get object info
            obj_info = self.metadata_db.get_object(bucket, key)
            if obj_info:
                # Delete from storage
                self.storage.delete(obj_info['storage_key'])
                
                # Delete from metadata
                self.metadata_db.delete_object(bucket, key)
            
            # S3 returns 204 even if object doesn't exist
            return Response('', status=204)
            
        except Exception as e:
            return self.error_response('InternalError', str(e), 500)
    
    def error_response(self, code, message, status):
        """Generate S3-compatible error response"""
        root = ET.Element('Error')
        ET.SubElement(root, 'Code').text = code
        ET.SubElement(root, 'Message').text = message
        ET.SubElement(root, 'RequestId').text = self.generate_request_id()
        
        xml_response = ET.tostring(root, encoding='unicode')
        return Response(xml_response, status=status, mimetype='application/xml')
    
    def is_valid_bucket_name(self, name):
        """Validate S3 bucket naming rules"""
        if len(name) < 3 or len(name) > 63:
            return False
        if not name.replace('-', '').replace('.', '').isalnum():
            return False
        if name.startswith('-') or name.endswith('-'):
            return False
        return True
    
    def generate_request_id(self):
        """Generate unique request ID"""
        return hashlib.md5(str(time.time()).encode()).hexdigest()[:16].upper()

# Storage backend interface
class StorageBackend:
    def put(self, key, data):
        """Store data with given key"""
        raise NotImplementedError
    
    def get(self, key):
        """Retrieve data by key"""
        raise NotImplementedError
    
    def delete(self, key):
        """Delete data by key"""
        raise NotImplementedError

# File system storage backend
class FileSystemStorage(StorageBackend):
    def __init__(self, base_path):
        self.base_path = base_path
        os.makedirs(base_path, exist_ok=True)
    
    def put(self, key, data):
        file_path = os.path.join(self.base_path, key)
        os.makedirs(os.path.dirname(file_path), exist_ok=True)
        with open(file_path, 'wb') as f:
            f.write(data)
    
    def get(self, key):
        file_path = os.path.join(self.base_path, key)
        try:
            with open(file_path, 'rb') as f:
                return f.read()
        except FileNotFoundError:
            return None
    
    def delete(self, key):
        file_path = os.path.join(self.base_path, key)
        try:
            os.remove(file_path)
        except FileNotFoundError:
            pass

# Usage
storage_backend = FileSystemStorage('/var/lib/object-storage')
s3_service = S3CompatibleStorage(storage_backend)

if __name__ == '__main__':
    s3_service.app.run(host='0.0.0.0', port=9000)`}
          </pre>

          <h3 style={styles.heading}>Client Implementation</h3>
          <p style={styles.paragraph}>
            Building S3-compatible clients requires proper handling of authentication, request signing, 
            error handling, and multipart uploads for large objects.
          </p>
          <pre style={styles.codeBlock}>
{`S3 Client Implementation:

import hashlib
import hmac
import urllib.parse
from datetime import datetime
import requests
import xml.etree.ElementTree as ET

class S3Client:
    def __init__(self, endpoint, access_key, secret_key, region='us-east-1'):
        self.endpoint = endpoint.rstrip('/')
        self.access_key = access_key
        self.secret_key = secret_key
        self.region = region
        self.service = 's3'
    
    def create_bucket(self, bucket_name):
        """Create a new bucket"""
        url = f"{self.endpoint}/{bucket_name}"
        headers = self.sign_request('PUT', f'/{bucket_name}', {})
        
        response = requests.put(url, headers=headers)
        if response.status_code not in [200, 201]:
            raise S3Error(f"Failed to create bucket: {response.text}")
        
        return True
    
    def put_object(self, bucket, key, data, content_type='binary/octet-stream', metadata=None):
        """Upload an object"""
        url = f"{self.endpoint}/{bucket}/{key}"
        
        headers = {
            'Content-Type': content_type,
            'Content-Length': str(len(data))
        }
        
        # Add custom metadata
        if metadata:
            for k, v in metadata.items():
                headers[f'x-amz-meta-{k}'] = str(v)
        
        # Sign request
        signed_headers = self.sign_request('PUT', f'/{bucket}/{key}', headers, data)
        headers.update(signed_headers)
        
        response = requests.put(url, data=data, headers=headers)
        if response.status_code not in [200, 201]:
            raise S3Error(f"Failed to put object: {response.text}")
        
        return {
            'etag': response.headers.get('ETag', '').strip('"'),
            'size': len(data)
        }
    
    def get_object(self, bucket, key):
        """Download an object"""
        url = f"{self.endpoint}/{bucket}/{key}"
        headers = self.sign_request('GET', f'/{bucket}/{key}', {})
        
        response = requests.get(url, headers=headers)
        if response.status_code == 404:
            raise S3Error(f"Object not found: {bucket}/{key}")
        elif response.status_code != 200:
            raise S3Error(f"Failed to get object: {response.text}")
        
        return {
            'data': response.content,
            'content_type': response.headers.get('Content-Type'),
            'size': int(response.headers.get('Content-Length', 0)),
            'etag': response.headers.get('ETag', '').strip('"'),
            'last_modified': response.headers.get('Last-Modified'),
            'metadata': self.extract_metadata(response.headers)
        }
    
    def delete_object(self, bucket, key):
        """Delete an object"""
        url = f"{self.endpoint}/{bucket}/{key}"
        headers = self.sign_request('DELETE', f'/{bucket}/{key}', {})
        
        response = requests.delete(url, headers=headers)
        if response.status_code not in [200, 204]:
            raise S3Error(f"Failed to delete object: {response.text}")
        
        return True
    
    def list_objects(self, bucket, prefix='', max_keys=1000):
        """List objects in bucket"""
        params = {'list-type': '2'}
        if prefix:
            params['prefix'] = prefix
        if max_keys != 1000:
            params['max-keys'] = str(max_keys)
        
        query_string = urllib.parse.urlencode(params)
        url = f"{self.endpoint}/{bucket}?{query_string}"
        headers = self.sign_request('GET', f'/{bucket}?{query_string}', {})
        
        response = requests.get(url, headers=headers)
        if response.status_code != 200:
            raise S3Error(f"Failed to list objects: {response.text}")
        
        # Parse XML response
        root = ET.fromstring(response.text)
        objects = []
        
        for contents in root.findall('Contents'):
            obj = {
                'key': contents.find('Key').text,
                'size': int(contents.find('Size').text),
                'etag': contents.find('ETag').text.strip('"'),
                'last_modified': contents.find('LastModified').text
            }
            objects.append(obj)
        
        return objects
    
    def multipart_upload(self, bucket, key, data, part_size=5*1024*1024):
        """Upload large object using multipart upload"""
        if len(data) <= part_size:
            # Use regular upload for small objects
            return self.put_object(bucket, key, data)
        
        # Initiate multipart upload
        upload_id = self.initiate_multipart_upload(bucket, key)
        
        try:
            parts = []
            part_number = 1
            offset = 0
            
            while offset < len(data):
                # Upload each part
                part_data = data[offset:offset + part_size]
                part_info = self.upload_part(
                    bucket, key, upload_id, part_number, part_data
                )
                parts.append(part_info)
                
                offset += part_size
                part_number += 1
            
            # Complete multipart upload
            return self.complete_multipart_upload(bucket, key, upload_id, parts)
            
        except Exception as e:
            # Abort multipart upload on error
            self.abort_multipart_upload(bucket, key, upload_id)
            raise e
    
    def sign_request(self, method, path, headers, payload=b''):
        """Sign request using AWS Signature Version 4"""
        now = datetime.utcnow()
        date_stamp = now.strftime('%Y%m%d')
        amz_date = now.strftime('%Y%m%dT%H%M%SZ')
        
        # Add required headers
        headers = headers.copy()
        headers['Host'] = self.endpoint.split('://')[-1]
        headers['X-Amz-Date'] = amz_date
        
        # Calculate payload hash
        payload_hash = hashlib.sha256(payload).hexdigest()
        headers['X-Amz-Content-Sha256'] = payload_hash
        
        # Create canonical request
        canonical_headers = ''
        signed_headers = ''
        
        sorted_headers = sorted(headers.items())
        for key, value in sorted_headers:
            canonical_headers += f"{key.lower()}:{value}\\n"
            signed_headers += f"{key.lower()};"
        
        signed_headers = signed_headers.rstrip(';')
        
        canonical_request = f"{method}\\n{path}\\n\\n{canonical_headers}\\n{signed_headers}\\n{payload_hash}"
        
        # Create string to sign
        algorithm = 'AWS4-HMAC-SHA256'
        credential_scope = f"{date_stamp}/{self.region}/{self.service}/aws4_request"
        string_to_sign = f"{algorithm}\\n{amz_date}\\n{credential_scope}\\n{hashlib.sha256(canonical_request.encode()).hexdigest()}"
        
        # Calculate signature
        signing_key = self.get_signature_key(date_stamp)
        signature = hmac.new(signing_key, string_to_sign.encode(), hashlib.sha256).hexdigest()
        
        # Create authorization header
        authorization = f"{algorithm} Credential={self.access_key}/{credential_scope}, SignedHeaders={signed_headers}, Signature={signature}"
        headers['Authorization'] = authorization
        
        return headers
    
    def get_signature_key(self, date_stamp):
        """Generate signing key"""
        key_date = hmac.new(f"AWS4{self.secret_key}".encode(), date_stamp.encode(), hashlib.sha256).digest()
        key_region = hmac.new(key_date, self.region.encode(), hashlib.sha256).digest()
        key_service = hmac.new(key_region, self.service.encode(), hashlib.sha256).digest()
        key_signing = hmac.new(key_service, b"aws4_request", hashlib.sha256).digest()
        return key_signing
    
    def extract_metadata(self, headers):
        """Extract custom metadata from response headers"""
        metadata = {}
        for key, value in headers.items():
            if key.lower().startswith('x-amz-meta-'):
                metadata[key[11:]] = value
        return metadata

class S3Error(Exception):
    pass

# Usage example
client = S3Client(
    endpoint='http://localhost:9000',
    access_key='your-access-key',
    secret_key='your-secret-key'
)

# Create bucket
client.create_bucket('my-bucket')

# Upload object
with open('large-file.zip', 'rb') as f:
    data = f.read()

result = client.multipart_upload(
    'my-bucket', 
    'uploads/large-file.zip', 
    data,
    metadata={'user-id': '123', 'upload-source': 'web'}
)

# Download object
obj = client.get_object('my-bucket', 'uploads/large-file.zip')
print(f"Downloaded {len(obj['data'])} bytes")

# List objects
objects = client.list_objects('my-bucket', prefix='uploads/')
for obj in objects:
    print(f"{obj['key']} - {obj['size']} bytes")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Features</h2>
          
          <h3 style={styles.heading}>Versioning and Lifecycle Management</h3>
          <p style={styles.paragraph}>
            Modern object storage systems provide advanced features like versioning, lifecycle policies, 
            and automated data management to optimize costs and meet compliance requirements.
          </p>
          <pre style={styles.codeBlock}>
{`Advanced Object Storage Features:

1. Object Versioning:
   - Keep multiple versions of same object
   - Protect against accidental deletion/modification
   - Each version has unique version ID
   - Can restore previous versions

Versioning Implementation:
{
  "bucket": "my-bucket",
  "key": "document.pdf",
  "versions": [
    {
      "version_id": "v1-2024-01-15-10:30:00",
      "size": 1024000,
      "etag": "abc123...",
      "last_modified": "2024-01-15T10:30:00Z",
      "is_latest": false
    },
    {
      "version_id": "v2-2024-01-16-14:20:00", 
      "size": 1056000,
      "etag": "def456...",
      "last_modified": "2024-01-16T14:20:00Z",
      "is_latest": true
    }
  ]
}

2. Lifecycle Policies:
   - Automatic data management
   - Transition to cheaper storage classes
   - Automatic deletion of old data
   - Rule-based management

Lifecycle Policy Example:
{
  "Rules": [
    {
      "ID": "LogsLifecycle",
      "Status": "Enabled",
      "Filter": {
        "Prefix": "logs/"
      },
      "Transitions": [
        {
          "Days": 30,
          "StorageClass": "STANDARD_IA"
        },
        {
          "Days": 90,
          "StorageClass": "GLACIER"
        },
        {
          "Days": 365,
          "StorageClass": "DEEP_ARCHIVE"
        }
      ],
      "Expiration": {
        "Days": 2555  // 7 years
      }
    },
    {
      "ID": "TempFilesCleanup",
      "Status": "Enabled", 
      "Filter": {
        "Prefix": "temp/"
      },
      "Expiration": {
        "Days": 1
      }
    }
  ]
}

3. Storage Classes:
   - STANDARD: Frequent access, high availability
   - STANDARD_IA: Infrequent access, lower cost
   - GLACIER: Archive storage, minutes to hours retrieval
   - DEEP_ARCHIVE: Long-term archive, 12+ hours retrieval

Storage Class Comparison:
┌─────────────┬──────────┬─────────────┬──────────────┬─────────────┐
│ Class       │ Durability│ Availability│ Retrieval    │ Cost        │
├─────────────┼──────────┼─────────────┼──────────────┼─────────────┤
│ STANDARD    │ 11 9's   │ 99.99%      │ Immediate    │ Highest     │
│ STANDARD_IA │ 11 9's   │ 99.9%       │ Immediate    │ Medium      │
│ GLACIER     │ 11 9's   │ 99.99%      │ 1-5 minutes  │ Low         │
│ DEEP_ARCHIVE│ 11 9's   │ 99.99%      │ 12 hours     │ Lowest      │
└─────────────┴──────────┴─────────────┴──────────────┴─────────────┘

4. Cross-Origin Resource Sharing (CORS):
   - Enable web browser access
   - Control which domains can access objects
   - Specify allowed methods and headers

CORS Configuration:
{
  "CORSRules": [
    {
      "AllowedOrigins": ["https://myapp.example.com"],
      "AllowedMethods": ["GET", "PUT", "POST", "DELETE"],
      "AllowedHeaders": ["*"],
      "ExposeHeaders": ["ETag", "x-amz-meta-*"],
      "MaxAgeSeconds": 3000
    }
  ]
}

5. Event Notifications:
   - Trigger actions on object events
   - Integration with message queues
   - Webhook notifications
   - Lambda function triggers

Event Configuration:
{
  "NotificationConfiguration": {
    "QueueConfigurations": [
      {
        "QueueArn": "arn:aws:sqs:us-east-1:123456789012:my-queue",
        "Events": ["s3:ObjectCreated:*"],
        "Filter": {
          "Key": {
            "FilterRules": [
              {
                "Name": "prefix",
                "Value": "uploads/"
              },
              {
                "Name": "suffix", 
                "Value": ".jpg"
              }
            ]
          }
        }
      }
    ]
  }
}

6. Server-Side Encryption:
   - Encrypt data at rest
   - Multiple encryption options
   - Key management integration
   - Transparent to clients

Encryption Options:
- SSE-S3: Service-managed keys
- SSE-KMS: Key Management Service
- SSE-C: Customer-provided keys

Encryption Headers:
PUT /bucket/object HTTP/1.1
x-amz-server-side-encryption: AES256
x-amz-server-side-encryption-kms-key-id: key-id
x-amz-server-side-encryption-context: {"project": "myapp"}

7. Access Control:
   - Bucket policies (resource-based)
   - Access Control Lists (ACLs)
   - IAM policies (identity-based)
   - Pre-signed URLs for temporary access

Bucket Policy Example:
{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "PublicReadGetObject",
      "Effect": "Allow",
      "Principal": "*",
      "Action": "s3:GetObject",
      "Resource": "arn:aws:s3:::my-bucket/public/*"
    },
    {
      "Sid": "RestrictToVPC",
      "Effect": "Deny",
      "Principal": "*",
      "Action": "s3:*",
      "Resource": [
        "arn:aws:s3:::my-bucket",
        "arn:aws:s3:::my-bucket/*"
      ],
      "Condition": {
        "StringNotEquals": {
          "aws:sourceVpc": "vpc-12345678"
        }
      }
    }
  ]
}`}
          </pre>

          <h3 style={styles.heading}>Performance Optimization</h3>
          <p style={styles.paragraph}>
            Object storage performance can be optimized through various techniques including request 
            patterns, caching, compression, and intelligent data placement strategies.
          </p>
          <pre style={styles.codeBlock}>
{`Performance Optimization Strategies:

1. Request Patterns:
   - Avoid sequential key patterns (hotspotting)
   - Use random prefixes for high-volume uploads
   - Batch operations when possible
   - Use multipart upload for large objects

Bad Pattern (Sequential):
logs/2024/01/15/00/file1.log
logs/2024/01/15/00/file2.log
logs/2024/01/15/00/file3.log
(All requests go to same partition)

Good Pattern (Random Prefix):
a1b2/logs/2024/01/15/00/file1.log
c3d4/logs/2024/01/15/00/file2.log  
e5f6/logs/2024/01/15/00/file3.log
(Distributed across partitions)

2. Transfer Acceleration:
   - Use CloudFront edge locations
   - Optimize for geographic distribution
   - Enable transfer acceleration
   - Use CDN for frequently accessed objects

3. Compression:
   - Compress objects before upload
   - Use appropriate compression algorithms
   - Consider CPU vs storage trade-offs

Compression Example:
import gzip
import json

def upload_compressed_json(client, bucket, key, data):
    # Compress JSON data
    json_str = json.dumps(data)
    compressed = gzip.compress(json_str.encode('utf-8'))
    
    # Upload with compression metadata
    client.put_object(
        bucket=bucket,
        key=key,
        data=compressed,
        content_type='application/json',
        metadata={
            'compression': 'gzip',
            'original-size': str(len(json_str))
        }
    )
    
    print(f"Compressed {len(json_str)} bytes to {len(compressed)} bytes")

4. Caching Strategies:
   - Use ETags for conditional requests
   - Implement client-side caching
   - Use CDN for static content
   - Cache metadata separately

ETag-based Caching:
def get_object_if_modified(client, bucket, key, cached_etag=None):
    # First, get object metadata
    try:
        head_response = client.head_object(bucket, key)
        current_etag = head_response['etag']
        
        if cached_etag and cached_etag == current_etag:
            return None  # Object hasn't changed
        
        # Object has changed, download it
        obj = client.get_object(bucket, key)
        return obj
        
    except S3Error as e:
        if "NoSuchKey" in str(e):
            return None
        raise

5. Parallel Operations:
   - Use concurrent uploads/downloads
   - Implement connection pooling
   - Batch multiple operations
   - Use async I/O when possible

Parallel Upload Example:
import concurrent.futures
import threading

class ParallelS3Client:
    def __init__(self, client, max_workers=10):
        self.client = client
        self.max_workers = max_workers
    
    def upload_files_parallel(self, bucket, files):
        """Upload multiple files in parallel"""
        results = []
        
        with concurrent.futures.ThreadPoolExecutor(max_workers=self.max_workers) as executor:
            # Submit all upload tasks
            future_to_file = {
                executor.submit(self.upload_file, bucket, file_info): file_info
                for file_info in files
            }
            
            # Collect results
            for future in concurrent.futures.as_completed(future_to_file):
                file_info = future_to_file[future]
                try:
                    result = future.result()
                    results.append({
                        'file': file_info,
                        'success': True,
                        'result': result
                    })
                except Exception as e:
                    results.append({
                        'file': file_info,
                        'success': False,
                        'error': str(e)
                    })
        
        return results
    
    def upload_file(self, bucket, file_info):
        """Upload single file"""
        with open(file_info['path'], 'rb') as f:
            data = f.read()
        
        return self.client.put_object(
            bucket=bucket,
            key=file_info['key'],
            data=data,
            content_type=file_info.get('content_type', 'binary/octet-stream')
        )

6. Monitoring and Metrics:
   - Track request latency
   - Monitor error rates
   - Measure throughput
   - Set up alerting

Performance Metrics:
{
  "metrics": {
    "request_latency_p95": "150ms",
    "request_latency_p99": "500ms", 
    "throughput_requests_per_second": 1000,
    "error_rate_percent": 0.1,
    "cache_hit_rate_percent": 85,
    "bandwidth_utilization_mbps": 100
  },
  "alerts": [
    {
      "metric": "request_latency_p95",
      "threshold": "1000ms",
      "action": "scale_up"
    },
    {
      "metric": "error_rate_percent", 
      "threshold": "1.0",
      "action": "investigate"
    }
  ]
}

7. Data Locality:
   - Store data close to compute
   - Use regional endpoints
   - Consider data gravity
   - Optimize for access patterns

Regional Strategy:
{
  "regions": {
    "us-east-1": {
      "primary": true,
      "use_cases": ["web_app", "analytics"]
    },
    "eu-west-1": {
      "primary": false,
      "use_cases": ["gdpr_compliance", "european_users"]
    },
    "ap-southeast-1": {
      "primary": false, 
      "use_cases": ["asian_users", "low_latency"]
    }
  },
  "replication": {
    "cross_region": true,
    "async": true,
    "selective": true
  }
}`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Design for scale:</strong> Use random key prefixes, implement proper partitioning, 
              and avoid hotspots in your access patterns.
            </li>
            <li style={styles.listItem}>
              <strong>Implement proper error handling:</strong> Handle network failures, implement 
              exponential backoff, and provide meaningful error messages to users.
            </li>
            <li style={styles.listItem}>
              <strong>Optimize for your use case:</strong> Choose appropriate storage classes, implement 
              lifecycle policies, and consider compression for frequently accessed data.
            </li>
            <li style={styles.listItem}>
              <strong>Security first:</strong> Use encryption at rest and in transit, implement proper 
              access controls, and regularly audit permissions.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor and measure:</strong> Track performance metrics, set up alerting, and 
              continuously optimize based on actual usage patterns.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for disaster recovery:</strong> Implement cross-region replication, test 
              backup and restore procedures, and document recovery processes.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Object storage and the S3 model provide a foundation for building scalable, durable storage 
            systems that can handle massive amounts of unstructured data. The key to success lies in 
            understanding the consistency model, implementing proper replication strategies, and optimizing 
            for your specific access patterns.
          </p>
          <p style={styles.paragraph}>
            Whether you're building your own object storage system or using existing services, focus on 
            proper API design, robust error handling, and comprehensive monitoring to create reliable 
            storage solutions that can scale with your application's needs.
          </p>
        </section>
      </main>
    </article>
  );
};

export default ObjectStorage;
